import { DomainError } from './errors.js';
import type { ChainQuorum, ChainStepKind } from './forms/approval-chain.js';
import { assertFormRuleWithoutOfficeStep } from './forms/no-office-approval.js';
import { parseSubject } from './forms/subject.js';
import { assertPostponementRuleWithoutOfficeStep } from './postponement-office.js';
import { type ApprovalReauthPolicy, assertReauthenticated } from './reauth-policy.js';

/**
 * Onay matrisinin bir satırı (mimari belge §7.1): hangi kayıt türünü kim girer, kaç adımda
 * kime onaya gider ve onay süresi. Onaylayan bir kişi değil, bir roldür (§7.3).
 */
export interface ApprovalRule {
  recordType: string;
  /** Girebilen roller; `any` her rol. */
  enteredBy: readonly string[] | 'any';
  /** Onay adımları: boş (onay yok), bir ya da iki adım. Her adımın onaylayıcı rolü. */
  steps: readonly string[];
  /** Onay süresi, saat; aşılınca uyarı (MEPC.312 4.3.10). */
  deadlineHours?: number;
  /**
   * Dört göz kuralının istisnası: aynı kişi hem girip hem onaylayabilir. Varsayılan kapalı;
   * açılırsa gerekçe zorunludur ve raporlarda görünür (mimari belge §7.1).
   */
  selfApprovalReason?: string;
  /**
   * Adım başına "ofiste" işareti (ADR 0026 §3): ofisteki adımın kararı `office_decisions`'a
   * yazılır ve gemiye iner. Verilmezse bütün adımlar gemidedir (geriye uyumlu).
   */
  officeSteps?: readonly boolean[];
  /**
   * Tutar kademesi, şirketin temel para biriminde (ADR 0026 §3): alt sınır dahil, üst sınır hariç
   * (sınırdaki tutar üst kademeye düşer; en sıkı okuma, sorular #70). Boşsa sınırsız.
   */
  minAmount?: number;
  maxAmount?: number;
  /**
   * Konu (ADR 0042 §9): yalnız `ism_form` kuralında; `folder:<node_key>` ya da `document:<uuid>`.
   * Mimari belge §7.1 ve gereksinim §4: onay matrisinde satır "ISM formu ya da formun
   * kategorisi"dir. Boşsa konusuz (bütün formlar) kural.
   */
  subject?: string;
  /**
   * Adım başına onaylayıcı roller (ADR 0071 §4): form zincirinde bir adımda iki rütbe olabilir
   * (hazırlayanlar, ofis). Verilmezse adımın tek rolü `steps[i]`'dir (matris kuralları). `steps[i]`
   * her zaman adımın ilk rolüdür (geriye uyumlu okuma).
   */
  stepRoles?: readonly (readonly string[])[];
  /** Adım başına kural: `all` her rol ayrı kişiyle imzalar, `any` biri yeter. Varsayılan `any`. */
  stepQuorum?: readonly ChainQuorum[];
  /** Adımların zincirdeki türü (görüntü: hazırlayan, ön onay, Master, ofis); matris kuralında yok. */
  stepKinds?: readonly ChainStepKind[];
  /**
   * Gönderime özgü yükselmeler (ADR 0091): kaydın tarafı bir gemi adımının onaylayıcı rolündeyse
   * adım ofise yükselmiştir. Matriste saklanmaz; `findApprovalRule` gönderimin taraf rolleriyle
   * kurar (`escalateSelfSubmitted`).
   */
  escalations?: readonly StepEscalation[];
}

/**
 * Bir gemi adımının ofise yükselmesi (ADR 0091):
 * - `office`: adım ofis adımı oldu; onaylayıcı şirketin yükselme rolü (Superintendent).
 * - `merged`: kuralda aynı roldeki bir ofis adımı zaten var; gemi adımı ona katılır, ayrı karar
 *   istemez (varsayılan matrisin "kaptanın kendi kaydı" kuralları gibi; ADR 0026 §3, ADR 0060 §8).
 * `shipRoles` adımın matristeki gemi rolleridir: yükselmeden önce gemide verilmiş karar (eski
 * sürüm) bu rollerle okunur ve korunur.
 */
export interface StepEscalation {
  step: number;
  to: 'office' | 'merged';
  shipRoles: readonly string[];
}

/** Adımın (1'den) onaylayıcı rolleri (ADR 0071 §4). */
export function stepRolesOf(rule: ApprovalRule, step: number): readonly string[] {
  const roles = rule.stepRoles?.[step - 1];
  if (roles && roles.length > 0) return roles;
  const role = rule.steps[step - 1];
  return role === undefined ? [] : [role];
}

/** Adım her rolün ayrı imzasını mı ister (`all` ve birden çok rol). */
export function stepNeedsEveryRole(rule: ApprovalRule, step: number): boolean {
  return rule.stepQuorum?.[step - 1] === 'all' && stepRolesOf(rule, step).length > 1;
}

/**
 * Onay matrisinde kuralı olabilen kayıt türleri: kapalı liste (ADR 0042 §24) [sorular #249].
 * Listede olmayan tür matrise girmez (`UNKNOWN_RECORD_TYPE`); yazım hatası sessizce "kuralsız"
 * bir türe dönüşmez. Yeni kayıt türü bu listeye, yetkilendiriciye (`authorizer.ts` `RECORD_TYPES`)
 * ve varsayılan matrise birlikte eklenir.
 */
export const APPROVAL_RECORD_TYPES = [
  // Aylık dinlenme saatleri (ADR 0021)
  'rest_hours_month',
  // Sefer raporu (ADR 0022)
  'voyage_report',
  // Malzeme talebi (ADR 0026)
  'requisition',
  // Şirket doküman revizyonu (ADR 0027 §4)
  'document_revision',
  // ISM form kaydı (ADR 0042)
  'ism_form',
  // Gemi kütüphanesi belge revizyonu (ADR 0036, ADR 0038 §10)
  'vessel_document_revision',
  // PMS: iş kaydı, erteleme talebi, aralık değişikliği (ADR 0029)
  'pms_work_record',
  'postponement_request',
  'maintenance_interval_change',
  // PMS: arıza ve hasar raporları (M7c)
  'defect_report',
  'breakdown_report',
  // HSE raporunun kapanışı (ADR 0049 §4): tür başına ayrı kural
  'hse_non_conformity',
  'hse_deficiency',
  'hse_near_miss',
  'hse_accident',
  // Günlük rapor (ADR 0051 §6)
  'daily_report',
  // Risk değerlendirmesi revizyonu (ADR 0050 §4)
  'risk_assessment',
  // Cash to Master talebi (ADR 0093 §3): varsayılan tek ofis adımı
  'ctm_request',
  // Yakıt yönetimi (ADR 0117 §12, Ek Y2): açılış bakiyesi, yakıt alımı, transfer ve diğer
  // hareketler; gemide başmühendis ya da kaptan onaylar, onay ROB defterine işler
  'fuel_opening_balance',
  'fuel_bunkering',
  'fuel_movement',
  // ROB düzeltme talebi (ADR 0117 §4, Ek Y4): gemi onayı + ofis adımı; son onayda gemi sunucusu
  // düzeltme hareketini yazar
  'fuel_adjustment',
] as const;
export type ApprovalRecordType = (typeof APPROVAL_RECORD_TYPES)[number];

/** Konu alabilen tek kayıt türü (ADR 0042 §9) [sorular #234]. */
const SUBJECT_RECORD_TYPE: ApprovalRecordType = 'ism_form';

/** Kademeli kural seçiminin tutarı (ADR 0026 §3). */
export interface ApprovalAmount {
  /** Toplam tahmini tutar; fiyatı girilmemiş satır varsa `null` (sorular #71). */
  value: number | null;
  currency: string;
  /** Şirketin temel para birimi; tutar yalnız bununla karşılaştırılır, kur tablosu yok. */
  baseCurrency: string;
}

/** Adım (1'den başlar) ofiste mi (ADR 0026 §3). */
export function isOfficeStep(rule: ApprovalRule, step: number): boolean {
  return rule.officeSteps?.[step - 1] === true;
}

const banded = (rule: ApprovalRule) => rule.minAmount !== undefined || rule.maxAmount !== undefined;

export interface ApprovalMatrix {
  version: number;
  rules: readonly ApprovalRule[];
  /**
   * Kendi kaydını gönderen onaylayıcının adımını ofiste karara bağlayan şirket rolleri (ADR 0091):
   * varsayılan Superintendent. Matris sürümünde saklanmaz; okuyan katman şirketin rollerinden
   * doldurur. Boşsa yalnız kuralın kendi ofis adımına katılma yapılır.
   */
  escalationRoleIds?: readonly string[];
}

export function validateApprovalMatrix(matrix: ApprovalMatrix, roleIds: readonly string[]): void {
  if (!Number.isInteger(matrix.version) || matrix.version < 1) {
    throw new DomainError('INVALID_MATRIX_VERSION');
  }
  const known = new Set(roleIds);
  const keys = new Map<string, ApprovalRule[]>();
  for (const rule of matrix.rules) {
    if (!(APPROVAL_RECORD_TYPES as readonly string[]).includes(rule.recordType)) {
      throw new DomainError('UNKNOWN_RECORD_TYPE', rule.recordType);
    }
    if (rule.steps.length > 2) throw new DomainError('TOO_MANY_APPROVAL_STEPS', rule.recordType);
    for (const roleId of [...rule.steps, ...(rule.enteredBy === 'any' ? [] : rule.enteredBy)]) {
      if (!known.has(roleId)) throw new DomainError('UNKNOWN_ROLE', roleId);
    }
    if (rule.enteredBy !== 'any' && rule.enteredBy.length === 0) {
      throw new DomainError('ENTERED_BY_REQUIRED', rule.recordType);
    }
    if (rule.deadlineHours !== undefined && !(rule.deadlineHours > 0)) {
      throw new DomainError('INVALID_DEADLINE', rule.recordType);
    }
    if (rule.selfApprovalReason !== undefined && rule.selfApprovalReason.trim() === '') {
      throw new DomainError('SELF_APPROVAL_REASON_REQUIRED', rule.recordType);
    }
    if ((rule.officeSteps ?? []).some((office, i) => office && rule.steps[i] === undefined)) {
      throw new DomainError('INVALID_OFFICE_STEP', rule.recordType);
    }
    // ISM formu ofis onayına gitmez (sahip kararı 2026-10-17; ADR 0073)
    assertFormRuleWithoutOfficeStep(rule);
    // Erteleme onayı gemide biter; ofis görür ve müdahale eder (sahip kararı 2026-10-23; ADR 0108)
    assertPostponementRuleWithoutOfficeStep(rule);
    const min = rule.minAmount ?? 0;
    const max = rule.maxAmount ?? Number.POSITIVE_INFINITY;
    if (!Number.isFinite(min) || min < 0 || !(max > min)) {
      throw new DomainError('INVALID_AMOUNT_BAND', rule.recordType);
    }
    if (rule.subject !== undefined) {
      // Konu yalnız ISM formu kuralında (ADR 0042 §9) ve biçimi denetlenir (§8) [sorular #234]
      if (rule.recordType !== SUBJECT_RECORD_TYPE) {
        throw new DomainError('SUBJECT_ONLY_FOR_FORMS', rule.recordType);
      }
      parseSubject(rule.subject);
    }
    const key = `${rule.recordType}|${rule.enteredBy === 'any' ? '*' : [...rule.enteredBy].sort().join(',')}|${rule.subject ?? ''}`;
    // Aynı türde aynı kişiye uyabilen kurallar (aynı ya da kesişen giren rolleri) ancak çakışmayan
    // tutar kademeleri olabilir; kademesiz kural 0–∞ sayılır. Böylece seçim kural sırasına bağlı
    // olmaz (sorular #70). Konusu farklı kural ayrı sayılır: seçimde konu grubu önce gelir
    // (ADR 0042 §9)
    for (const [otherKey, others] of keys) {
      for (const other of others) {
        if (
          other.recordType !== rule.recordType ||
          other.subject !== rule.subject ||
          !sameSubmitters(rule, other)
        ) {
          continue;
        }
        if (!banded(rule) && !banded(other)) throw new DomainError('DUPLICATE_RULE', key);
        const otherMin = other.minAmount ?? 0;
        const otherMax = other.maxAmount ?? Number.POSITIVE_INFINITY;
        if (min < otherMax && otherMin < max) {
          throw new DomainError('OVERLAPPING_AMOUNT_BANDS', otherKey === key ? key : otherKey);
        }
      }
    }
    keys.set(key, [...(keys.get(key) ?? []), rule]);
  }
  // "Herkes" kademeleri bütün tutarları boşluksuz kapsar: boşluktaki tutar için kural olmazdı
  // (en sıkı okuma; açık rollerin kademeleri boşluklu olabilir, kapsamadığı tutar "herkes"e düşer)
  const fallbacks = new Map<string, ApprovalRule[]>();
  for (const rule of matrix.rules) {
    if (rule.enteredBy !== 'any' || !banded(rule)) continue;
    const group = `${rule.recordType}|${rule.subject ?? ''}`;
    fallbacks.set(group, [...(fallbacks.get(group) ?? []), rule]);
  }
  for (const [group, rules] of fallbacks) {
    const recordType = group.slice(0, group.indexOf('|'));
    let covered = 0;
    for (const rule of [...rules].sort((a, b) => (a.minAmount ?? 0) - (b.minAmount ?? 0))) {
      if ((rule.minAmount ?? 0) > covered) throw new DomainError('AMOUNT_BAND_GAP', recordType);
      covered = Math.max(covered, rule.maxAmount ?? Number.POSITIVE_INFINITY);
    }
    if (covered !== Number.POSITIVE_INFINITY) {
      throw new DomainError('AMOUNT_BAND_GAP', recordType);
    }
  }
}

/** İki kural aynı kişiye uyabilir mi: ikisi de `any` ya da açık giren rolleri kesişiyor. */
function sameSubmitters(a: ApprovalRule, b: ApprovalRule): boolean {
  if (a.enteredBy === 'any' || b.enteredBy === 'any') return a.enteredBy === b.enteredBy;
  const other = b.enteredBy;
  return a.enteredBy.some((role) => other.includes(role));
}

/**
 * Kaydı giren kişinin rollerine uyan kural. Konu zinciri verilirse (ADR 0042 §9) önce zincirin ilk
 * konusunun kuralları, onlardan hiçbiri uymazsa sonraki konununkiler, en son konusuz kurallar
 * aranır; zincirde olmayan konunun kuralı hiç sayılmaz. Seçilen grupta aşağıdaki seçim aynen
 * geçerlidir. Rolü açıkça sayan kural `any` kuralının önüne geçer
 * (ör. "kaptanın girdiği form" ile "herhangi bir zabitin girdiği form" ayrı kurallar).
 * Kural yoksa kayıt onaya gönderilemez: eksik matris sessizce "onaysız" sayılmaz.
 *
 * Tutar kademeli kurallarda (ADR 0026 §3) kural tutara göre seçilir: alt sınır dahil, üst sınır
 * hariç. Tutar bilinmiyorsa (fiyatsız satır) ya da para birimi temel para biriminden farklıysa en
 * yüksek (üst sınırsız) kademe uygulanır (kur tablosu yok; en sıkı okuma). Açık rollerin
 * kademeleri tutarı kapsamıyorsa `any` kademelerine bakılır.
 *
 * `submitterRoleIds` kaydın taraflarının (gönderen; talepte talep eden ve oluşturan da) rolleridir.
 * Birden çok kural uyarsa önce bu rollerin hiçbir gemi adımında onaylayıcı olmadığı kural seçilir
 * (ADR 0031 ek): taraf kendi kaydını onaylayamaz, öteki kural kalıcı takılırdı.
 */
export function findApprovalRule(
  matrix: ApprovalMatrix,
  recordType: string,
  submitterRoleIds: readonly string[],
  amount?: ApprovalAmount,
  subjects: readonly string[] = [],
): ApprovalRule {
  const ofType = matrix.rules.filter((rule) => rule.recordType === recordType);
  const levels = [...new Set(subjects)].map((subject) =>
    ofType.filter((rule) => rule.subject === subject),
  );
  levels.push(ofType.filter((rule) => rule.subject === undefined));
  for (const candidates of levels) {
    const explicit = candidates.filter(
      (rule) =>
        rule.enteredBy !== 'any' && rule.enteredBy.some((r) => submitterRoleIds.includes(r)),
    );
    const fallback = candidates.filter((rule) => rule.enteredBy === 'any');
    for (const group of [explicit, fallback]) {
      if (group.length === 0) continue;
      const escalate = (rule: ApprovalRule) =>
        escalateSelfSubmitted(rule, submitterRoleIds, matrix.escalationRoleIds ?? []);
      if (!group.some(banded)) return escalate(strictest(group, submitterRoleIds) as ApprovalRule);
      if (!amount) throw new DomainError('APPROVAL_AMOUNT_REQUIRED', recordType);
      const rule = bandFor(group, amount, submitterRoleIds);
      if (rule) return escalate(rule);
    }
  }
  throw new DomainError('APPROVAL_RULE_MISSING', recordType);
}

/**
 * Ofiste karara bağlanabilen kayıt türleri (ADR 0091): ofis karar yolu (`office_decisions`) olan
 * türler. ISM formu ofis onayına gitmez (ADR 0073); dinlenme saatleri, günlük rapor, PMS iş kaydı
 * ve belgeler yükselmez, onlarda gönderim eskisi gibi denetlenir (`NO_ELIGIBLE_APPROVER`).
 */
export const ESCALATABLE_RECORD_TYPES: readonly ApprovalRecordType[] = [
  'requisition',
  // Erteleme ofise yükselmez: onay gemide biter, kaptanın kendi talebi öz onaylıdır (ADR 0108 §1)
  'maintenance_interval_change',
  'defect_report',
  'breakdown_report',
  'hse_non_conformity',
  'hse_deficiency',
  'hse_near_miss',
  'hse_accident',
  'risk_assessment',
  // CTM talebi (ADR 0093 §3): varsayılan kural zaten tek ofis adımlıdır; şirket kurala gemi adımı
  // eklerse kaptanın kendi talebi o adımda ofise yükselir
  'ctm_request',
];

/**
 * Kendi kaydını gönderen onaylayıcının adımını ofise yükseltir (ADR 0091). Kaydın taraflarından
 * biri (gönderen, talep eden, oluşturan; `partyRoleIds` onların rolleri) bir gemi adımının
 * onaylayıcı rolündeyse o kişi dört göz kuralıyla bu adımı onaylayamaz; gemide aynı rolde başka
 * biri olsa bile (devir, ikinci hesap) kaydın sahibi olan rütbenin işi ofise gider (en sıkı okuma;
 * emsal: AMOS, ShipManager'da kaptanın talebi ofis onayına çıkar):
 * - kuralda yükselme rolünü taşıyan bir ofis adımı varsa adım ona katılır (`merged`);
 * - yoksa adım ofis adımı olur, onaylayıcı yükselme rolüdür (`office`);
 * - yükselme rolü de ofis adımı da yoksa kural değişmez.
 * Öz onay istisnası (`selfApprovalReason`) olan kural ve ofis karar yolu olmayan tür yükselmez.
 * Dört göz gevşemez: taraf ofiste de onaylayamaz, iki adımı aynı kişi onaylayamaz.
 */
export function escalateSelfSubmitted(
  rule: ApprovalRule,
  partyRoleIds: readonly string[],
  escalationRoleIds: readonly string[],
): ApprovalRule {
  if (
    rule.selfApprovalReason !== undefined ||
    rule.stepKinds !== undefined ||
    !(ESCALATABLE_RECORD_TYPES as readonly string[]).includes(rule.recordType)
  ) {
    return rule;
  }
  const steps = [...rule.steps];
  const office = rule.steps.map((_, i) => isOfficeStep(rule, i + 1));
  const roles = rule.steps.map((_, i) => [...stepRolesOf(rule, i + 1)]);
  const escalations: StepEscalation[] = [];
  for (let i = 0; i < steps.length; i += 1) {
    const shipRoles = roles[i] ?? [];
    if (office[i] || !shipRoles.some((role) => partyRoleIds.includes(role))) continue;
    const target =
      escalationRoleIds.length > 0
        ? escalationRoleIds
        : (roles.find((_, j) => office[j] === true) ?? []);
    const mergesInto = roles.findIndex(
      (stepRoles, j) => office[j] === true && stepRoles.some((role) => target.includes(role)),
    );
    if (mergesInto >= 0) {
      escalations.push({ step: i + 1, to: 'merged', shipRoles });
      continue;
    }
    if (target.length === 0) continue;
    steps[i] = target[0] as string;
    roles[i] = [...target];
    office[i] = true;
    escalations.push({ step: i + 1, to: 'office', shipRoles });
  }
  if (escalations.length === 0) return rule;
  const { stepRoles: _roles, ...rest } = rule;
  const escalated: ApprovalRule = { ...rest, steps, officeSteps: office, escalations };
  if (rule.stepRoles !== undefined || roles.some((list) => list.length > 1)) {
    escalated.stepRoles = roles;
  }
  return escalated;
}

/**
 * Adıma gemide yazılabilen kararın rolleri: gemi adımında adımın rolleri, ofis adımında yok;
 * yükselen adımda yükselmeden önceki gemi rolleri (eski sürüm gemi kararı; ADR 0091).
 */
export function shipRolesAt(rule: ApprovalRule, step: number): readonly string[] {
  const escalation = escalationOf(rule, step);
  if (escalation) return escalation.shipRoles;
  return isOfficeStep(rule, step) ? [] : stepRolesOf(rule, step);
}

/** Adımın yükselmesi (ADR 0091); yükselmemişse `undefined`. */
export function escalationOf(rule: ApprovalRule, step: number): StepEscalation | undefined {
  return rule.escalations?.find((escalation) => escalation.step === step);
}

const officeStepCount = (rule: ApprovalRule) =>
  rule.steps.filter((_, i) => isOfficeStep(rule, i + 1)).length;

/**
 * Kuralın kanonik anahtarı: matristeki sırasından ve dizilerin yazılış sırasından bağımsız.
 * Öteki ölçütler eşitse seçimi bu belirler (ADR 0031 ek, düzeltme denetimi).
 */
function ruleKey(rule: ApprovalRule): string {
  return JSON.stringify([
    rule.recordType,
    rule.enteredBy === 'any' ? '*' : [...rule.enteredBy].sort(),
    rule.steps,
    rule.steps.map((_, i) => isOfficeStep(rule, i + 1)),
    rule.minAmount ?? 0,
    rule.maxAmount ?? null,
    rule.deadlineHours ?? null,
    rule.selfApprovalReason ?? null,
    rule.subject ?? null,
  ]);
}

/** Kuralın bir gemi adımını kaydın taraflarının rollerinden biri mi onaylar. */
function partyApproves(rule: ApprovalRule, partyRoleIds: readonly string[]): boolean {
  return rule.steps.some(
    (_, i) =>
      !isOfficeStep(rule, i + 1) &&
      stepRolesOf(rule, i + 1).some((role) => partyRoleIds.includes(role)),
  );
}

/**
 * Uyan kuralların en sıkısı; seçim kural sırasına bağlı değildir (ADR 0031 ek): önce kaydın
 * taraflarının rollerinin hiçbir gemi adımında onaylayıcı olmadığı kural, sonra çok adımlı, çok
 * ofis adımlı, alt sınırı büyük olan, dört göz istisnası olmayan; hepsi eşitse kanonik anahtarı
 * küçük olan.
 */
function strictest(
  rules: readonly ApprovalRule[],
  partyRoleIds: readonly string[],
): ApprovalRule | undefined {
  const keyOrder = (a: ApprovalRule, b: ApprovalRule) => {
    const [x, y] = [ruleKey(a), ruleKey(b)];
    return x < y ? -1 : x > y ? 1 : 0;
  };
  return [...rules].sort(
    (a, b) =>
      Number(partyApproves(a, partyRoleIds)) - Number(partyApproves(b, partyRoleIds)) ||
      b.steps.length - a.steps.length ||
      officeStepCount(b) - officeStepCount(a) ||
      (b.minAmount ?? 0) - (a.minAmount ?? 0) ||
      Number(a.selfApprovalReason !== undefined) - Number(b.selfApprovalReason !== undefined) ||
      keyOrder(a, b),
  )[0];
}

/**
 * Gruptaki kademe. Tutar bilinmiyorsa (fiyatsız satır, başka para birimi) tutar sınırsız büyük
 * sayılır: yalnız üst sınırı olmayan kademe uyar; grupta yoksa bilinen büyük tutar gibi bir
 * sonraki gruba (`any`) bakılır (sorular #71, #76). Birden çok kademe uyarsa (iki rolü olan kişi)
 * `strictest` seçer; seçim kural sırasına bağlı olmaz (ADR 0031 ek).
 */
function bandFor(
  rules: readonly ApprovalRule[],
  amount: ApprovalAmount,
  partyRoleIds: readonly string[],
): ApprovalRule | undefined {
  const unknown = amount.value === null || amount.currency !== amount.baseCurrency;
  const value = amount.value ?? Number.POSITIVE_INFINITY;
  return strictest(
    rules.filter((rule) =>
      unknown
        ? rule.maxAmount === undefined
        : value >= (rule.minAmount ?? 0) && value < (rule.maxAmount ?? Number.POSITIVE_INFINITY),
    ),
    partyRoleIds,
  );
}

/** Gemide onaylayabilecek kişi: o günkü gemi rolleriyle (mimari belge §5.3). */
export interface ApproverCandidate {
  userId: string;
  roleIds: readonly string[];
}

/**
 * Gönderimde seçilen kuralın her gemi adımını karara bağlayacak biri var mı (ADR 0031 ek): her
 * gemi adımı için o role gemi gününde atanmış, etkin ve kaydın tarafı (gönderen, talep eden,
 * oluşturan) olmayan bir kişi; iki gemi adımı iki ayrı kişi (`SAME_APPROVER_FORBIDDEN`). Kuralın
 * dört göz istisnası (`selfApprovalReason`) varsa taraf da sayılır. Yoksa kayıt gönderilemez
 * (`NO_ELIGIBLE_APPROVER`) ve taslakta kalır: gönderilse hiçbir zaman karara bağlanamazdı.
 * Ofis adımları gemide denetlenemez; onların kişisi ofistedir.
 * @param people O gün gemiye atanmış etkin kişiler ve rolleri.
 * @param parties Kaydın tarafları (kullanıcı kimlikleri).
 */
export function assertApproversAvailable(
  rule: ApprovalRule,
  people: readonly ApproverCandidate[],
  parties: readonly string[],
  /**
   * Gönderen (ADR 0071 §3): hazırlayan adımında yalnız o taraf sayılır; taslağı açan öteki
   * hazırlayan kendi hazırlayan imzasını atabilir. Verilmezse bütün taraflar.
   */
  submitter?: string,
): void {
  // İmza yerleri: "hepsi" adımında her rol ayrı yer, öteki adımda adımın rollerinden biri
  // (ADR 0071 §4)
  const steps = rule.steps.flatMap((_, i) => {
    const step = i + 1;
    // Ofise yükselen ya da ofis adımına katılan adım gemide aranmaz (ADR 0091)
    if (isOfficeStep(rule, step) || escalationOf(rule, step) !== undefined) return [];
    const roles = stepRolesOf(rule, step);
    return stepNeedsEveryRole(rule, step)
      ? roles.map((role) => ({ step, roles: [role] }))
      : [{ step, roles }];
  });
  const eligible = steps.map(({ step, roles }) => {
    const excluded =
      submitter !== undefined && rule.stepKinds?.[step - 1] === 'prepare' ? [submitter] : parties;
    return people
      .filter(
        (person) =>
          roles.some((role) => person.roleIds.includes(role)) &&
          (rule.selfApprovalReason !== undefined || !excluded.includes(person.userId)),
      )
      .map((person) => person.userId);
  });
  // Her imza yerine ayrı kişi (zincirde en çok yedi yer; küçük arama)
  const assignable = (index: number, used: readonly string[]): boolean =>
    index === steps.length ||
    (eligible[index] ?? []).some(
      (userId) => !used.includes(userId) && assignable(index + 1, [...used, userId]),
    );
  if (!assignable(0, [])) {
    const empty = steps.find((_, i) => eligible[i]?.length === 0) ?? steps[steps.length - 1];
    throw new DomainError('NO_ELIGIBLE_APPROVER', `adım ${empty?.step}`);
  }
}

/** Değiştirilemez gönderim kaydı (`submissions`, mimari belge §7.3). */
export interface Submission {
  id: string;
  recordId: string;
  revision: number;
  contentHash: string;
  submittedBy: string;
  submitterRoleIds: readonly string[];
  matrixVersion: number;
  /** ISO-8601 UTC. */
  at: string;
}

/** Değiştirilemez onay kaydı (`approvals`, mimari belge §7.3). */
export interface Approval {
  id: string;
  submissionId: string;
  step: number;
  decision: 'approved' | 'returned';
  reason: string | null;
  approverId: string;
  approverRoleId: string;
  matrixVersion: number;
  contentHash: string;
  at: string;
}

export type ApprovalState =
  | { status: 'draft' }
  | {
      status: 'pending';
      step: number;
      submission: Submission;
      since: string;
      /** Bekleyen adım ofiste (ADR 0026 §3): karar bir sonraki senkronda gemiye iner. */
      office?: true;
      /**
       * Bu gönderimin onayları (önceki adımlar ve "hepsi" adımında bu adımın verilmiş imzaları):
       * aynı kişi iki imza atamaz (dört göz).
       */
      approvals: Approval[];
      /** Bekleyen adımda imzası beklenen roller (ADR 0071 §4); "biri yeter"de adımın rolleri. */
      awaiting?: string[];
      /** Bekleyen adım, kaydı gönderen onaylayıcı yüzünden ofise yükseldi (ADR 0091). */
      escalated?: true;
    }
  | { status: 'returned'; submission: Submission; returned: Approval }
  | { status: 'approved'; submission: Submission; approvals: Approval[] }
  /** Onaydan sonra içerik değişti; onay geçersiz, kayıt yeniden onaya düşer (MEPC.312 4.3.9). */
  | { status: 'reapproval_pending'; submission: Submission; approvals: Approval[] };

/**
 * Kaydın onay durumunu değiştirilemez gönderim ve onay kayıtlarından türetir (mimari belge §7.2).
 * Durum ayrıca saklanmaz; böylece geçmiş ile durum hiçbir zaman çelişmez.
 *
 * İki kaynak (ADR 0026 §3): gemideki adımların kararları `approvals`'tan, ofisteki adımlarınki
 * `office_decisions`'tan okunur; yanlış tarafa yazılmış karar sayılmaz. Ofis kararı ancak
 * gönderilen içeriğin hash'ini taşıyorsa geçerlidir. Her adımın tek kararı vardır: o adımın
 * tarafındaki ilk kayıt; aynı adımın ikinci kaydı ve kural dışı adımın onayı sayılmaz. Kayıt
 * ancak her adımın onayı varsa onaylanır (sayım değil, adım adım; bozuk ya da kurcalanmış bir
 * gemi paketi ofis adımını gemi kaydıyla dolduramaz).
 * @param submissions Kaydın gönderimleri, eskiden yeniye.
 * @param approvals Bu gönderimlere ait gemideki onay kayıtları.
 * @param rule Son gönderimin matris sürümündeki kural: akış gönderim anındaki kuralla yürür.
 * @param officeDecisions Bu gönderimlere ait ofis kararları.
 */
export function deriveApprovalState(
  currentContentHash: string,
  submissions: readonly Submission[],
  approvals: readonly Approval[],
  rule: ApprovalRule,
  officeDecisions: readonly Approval[] = [],
): ApprovalState {
  const submission = submissions[submissions.length - 1];
  if (!submission) return { status: 'draft' };
  const earliest = (list: readonly Approval[]) =>
    [...list].sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id))[0];
  const officeDecisionAt = (step: number, role?: string): Approval | undefined =>
    earliest(
      officeDecisions.filter(
        (decision) =>
          decision.submissionId === submission.id &&
          decision.step === step &&
          decision.contentHash === submission.contentHash &&
          (role === undefined || decision.approverRoleId === role),
      ),
    );
  // Yükselmeden önce gemide verilmiş karar (eski sürüm; ADR 0091): adımın matristeki gemi
  // rolüyle yazılmışsa korunur
  const legacyShipDecision = (step: number): Approval | undefined => {
    const escalation = escalationOf(rule, step);
    if (!escalation) return undefined;
    return earliest(
      approvals.filter(
        (approval) =>
          approval.submissionId === submission.id &&
          approval.step === step &&
          escalation.shipRoles.includes(approval.approverRoleId),
      ),
    );
  };
  // `role` verilirse yalnız o rolün kararı ("hepsi" adımı; ADR 0071 §4)
  const decisionAt = (step: number, role?: string): Approval | undefined =>
    escalationOf(rule, step) !== undefined
      ? earliest(
          [
            legacyShipDecision(step),
            escalationOf(rule, step)?.to === 'office' ? officeDecisionAt(step, role) : undefined,
          ].filter((d): d is Approval => d !== undefined),
        )
      : isOfficeStep(rule, step)
        ? officeDecisionAt(step, role)
        : earliest(
            approvals.filter(
              (approval) =>
                approval.submissionId === submission.id &&
                approval.step === step &&
                (role === undefined || approval.approverRoleId === role),
            ),
          );

  const approved: Approval[] = [];
  let pendingStep: number | undefined;
  let awaiting: string[] = [];
  for (let step = 1; step <= rule.steps.length; step += 1) {
    const roles = stepRolesOf(rule, step);
    if (!stepNeedsEveryRole(rule, step)) {
      const decision = decisionAt(step);
      // Ofis adımına katılan gemi adımı ayrı karar istemez (ADR 0091)
      if (!decision && escalationOf(rule, step)?.to === 'merged') continue;
      if (!decision) {
        pendingStep = step;
        awaiting = [...roles];
        break;
      }
      if (decision.decision === 'returned')
        return { status: 'returned', submission, returned: decision };
      approved.push(decision);
      continue;
    }
    // "Hepsi" adımı (ADR 0071 §4): her rolün ilk kararı; bir iade bütün kaydı iade eder
    const byRole = roles.map((role) => decisionAt(step, role));
    const returned = earliest(
      byRole.filter((d): d is Approval => d !== undefined && d.decision === 'returned'),
    );
    if (returned) return { status: 'returned', submission, returned };
    approved.push(...byRole.filter((d): d is Approval => d !== undefined));
    const missing = roles.filter((_, i) => byRole[i] === undefined);
    if (missing.length > 0) {
      pendingStep = step;
      awaiting = missing;
      break;
    }
  }
  // Kuralın adımlarından sonraki gemi iade kaydı kesinleşmiş kaydın yeniden açılmasıdır
  // (ADR 0022 §3); yalnız iade olarak sayılır, onay olarak asla
  const reopened = earliest(
    approvals.filter(
      (approval) =>
        approval.submissionId === submission.id &&
        approval.step > rule.steps.length &&
        approval.decision === 'returned',
    ),
  );
  if (reopened) return { status: 'returned', submission, returned: reopened };

  const changed = currentContentHash !== submission.contentHash;
  if (pendingStep === undefined) {
    return changed
      ? { status: 'reapproval_pending', submission, approvals: approved }
      : { status: 'approved', submission, approvals: approved };
  }
  // Onay hash'i eşleşmeyen içerik için geçersizdir (mimari belge §7.3)
  if (changed) return { status: 'reapproval_pending', submission, approvals: approved };
  const last = approved[approved.length - 1];
  return {
    status: 'pending',
    step: pendingStep,
    submission,
    since: last ? last.at : submission.at,
    ...(isOfficeStep(rule, pendingStep) ? { office: true as const } : {}),
    approvals: approved,
    awaiting,
    ...(escalationOf(rule, pendingStep) ? { escalated: true as const } : {}),
  };
}

export interface SubmitInput {
  state: ApprovalState;
  rule: ApprovalRule;
  id: string;
  recordId: string;
  revision: number;
  contentHash: string;
  submitter: { userId: string; roleIds: readonly string[] };
  matrixVersion: number;
  now: string;
}

/** Kaydı onaya gönderir; yeni değiştirilemez gönderim kaydını döner. */
export function submitForApproval(input: SubmitInput): Submission {
  const { state, rule, submitter } = input;
  if (state.status === 'pending' || state.status === 'approved') {
    throw new DomainError('ALREADY_SUBMITTED', state.status);
  }
  if (rule.enteredBy !== 'any' && !rule.enteredBy.some((r) => submitter.roleIds.includes(r))) {
    throw new DomainError('NOT_ALLOWED_TO_SUBMIT', rule.recordType);
  }
  return {
    id: input.id,
    recordId: input.recordId,
    revision: input.revision,
    contentHash: input.contentHash,
    submittedBy: submitter.userId,
    submitterRoleIds: [...submitter.roleIds],
    matrixVersion: input.matrixVersion,
    at: input.now,
  };
}

export interface DecideInput {
  state: ApprovalState;
  rule: ApprovalRule;
  id: string;
  decision: 'approved' | 'returned';
  reason?: string;
  approver: { userId: string; roleIds: readonly string[] };
  /** Onaylanan içeriğin hash'i: onaylayanın gördüğü içerik. */
  contentHash: string;
  /** Onay anında parola yeniden istendi ve doğrulandı mı. */
  reauthenticated: boolean;
  /**
   * Şirketin onay ayarı (ADR 0056 §4, ADR 0075): ayar kapalıyken oturum yeter, açıkken her türde
   * parola istenir. Verilmezse en sıkı okuma: parola zorunlu.
   */
  reauthPolicy?: ApprovalReauthPolicy;
  matrixVersion: number;
  now: string;
  /**
   * Kararın verildiği yer (ADR 0026 §3): ofis adımına yalnız ofiste (`office_decisions`), gemi
   * adımına yalnız gemide karar verilir. Varsayılan gemi.
   */
  at?: 'ship' | 'office';
  /**
   * Kaydı girenler (ör. talep eden, oluşturan; ADR 0026 §3 "giren ile onaylayan aynı kişi
   * olamaz"): gönderenle birlikte onaylayamaz. Kuralın `selfApprovalReason` istisnası bunu da
   * kapsar.
   */
  recordParties?: readonly string[];
  /**
   * Onaylayanın gördüğü gönderim ve adım: bekleyen adım başkaysa (bayat ekran, tekrar gönderilen
   * istek) karar yazılmaz (`STALE_DECISION`). Verilmezse denetlenmez.
   */
  expected?: { submissionId: string; step: number };
  /**
   * Süresi geçmiş adımda (`isApprovalOverdue`) kararın açıklaması zorunlu mu
   * (`LATE_REASON_REQUIRED`); açıklama karar kaydının gerekçesine yazılır. Ajan kararı,
   * MEPC.312(74) 4.3.10'dan esinlenen daha sıkı kural (ADR 0042 §16) [sorular #241]; M8b'de yalnız
   * `ism_form` için açılır. Verilmezse aranmaz (geriye uyumlu).
   */
  lateReasonRequired?: boolean;
}

type PendingApprovalState = Extract<ApprovalState, { status: 'pending' }>;

/** Kararı yapısal olarak engelleyen kural: `DomainError`'ın kodu ve iç iletisi. */
interface Blocker {
  code: string;
  message?: string;
}

/** Adımın tarafı (ADR 0026 §3): ofis adımına yalnız ofiste, gemi adımına yalnız gemide. */
function sideBlocker(
  rule: ApprovalRule,
  step: number,
  at: 'ship' | 'office' | undefined,
): Blocker | null {
  const office = isOfficeStep(rule, step);
  if (office && at !== 'office') return { code: 'OFFICE_STEP', message: `adım ${step}` };
  if (!office && at === 'office') return { code: 'NOT_OFFICE_STEP', message: `adım ${step}` };
  return null;
}

/**
 * Onaylayanın adımdaki rolü ya da onu engelleyen kural: rol, "hepsi" adımında imzalanmış rol,
 * giren-onaylayan (öz onay istisnası) ve iki adımı aynı kişinin onaylaması (dört göz).
 */
function approverRole(
  state: PendingApprovalState,
  rule: ApprovalRule,
  approver: { userId: string; roleIds: readonly string[] },
  recordParties: readonly string[] | undefined,
): { roleId: string } | Blocker {
  // Adımın rollerinden imzası beklenen ve onaylayanda olan ilki; "hepsi" adımında imzalanmış rol
  // yeniden imzalanmaz (ADR 0071 §4). Sırası gelmemiş adımın rolü `NOT_APPROVER`
  const signed = new Set(
    state.approvals.filter((a) => a.step === state.step).map((a) => a.approverRoleId),
  );
  const open = stepNeedsEveryRole(rule, state.step)
    ? stepRolesOf(rule, state.step).filter((role) => !signed.has(role))
    : stepRolesOf(rule, state.step);
  const roleId = open.find((role) => approver.roleIds.includes(role));
  if (roleId === undefined) {
    // "Hepsi" adımının imzalanmış rolünü taşıyan kişinin ikinci imzası dört göz ihlalidir
    if (
      stepRolesOf(rule, state.step).some((role) => approver.roleIds.includes(role)) &&
      state.approvals.some((a) => a.step === state.step && a.approverId === approver.userId)
    ) {
      return { code: 'SAME_APPROVER_FORBIDDEN', message: `adım ${state.step}` };
    }
    return { code: 'NOT_APPROVER', message: `adım ${state.step}` };
  }
  // Hazırlayan adımında (ADR 0071 §3) taslağı açan öteki hazırlayan imzalayabilir: yalnız gönderen
  // taraftır
  const preparing = rule.stepKinds?.[state.step - 1] === 'prepare';
  const entered =
    approver.userId === state.submission.submittedBy ||
    (!preparing && (recordParties ?? []).includes(approver.userId));
  if (entered && rule.selfApprovalReason === undefined) {
    return { code: 'SELF_APPROVAL_FORBIDDEN' };
  }
  // Bir gönderimin iki adımını aynı kişi onaylayamaz: iki adımlı zincir iki kişidir (dört göz;
  // ADR 0026 §3, sorular #75). Gemide ve ofiste ayrı rolü olan kişi de (ADR 0012 §4). İstisnası
  // yoktur; `selfApprovalReason` giren-onaylayan istisnasıdır, bunu kapsamaz
  if (state.approvals.some((approval) => approval.approverId === approver.userId)) {
    return { code: 'SAME_APPROVER_FORBIDDEN', message: `adım ${state.step}` };
  }
  return { roleId };
}

/** Kararın yapısal girdileri: `decideApproval`'ın parola, hash ve gerekçe dışındaki girdileri. */
export interface MayDecideInput {
  state: ApprovalState;
  rule: ApprovalRule;
  approver: { userId: string; roleIds: readonly string[] };
  at?: 'ship' | 'office';
  recordParties?: readonly string[];
}

/**
 * Kişinin şimdi karar vermesini engelleyen kuralın kodu; engel yoksa `null` (ADR 0156). Kodlar
 * `decideApproval`'ınkilerle aynıdır: `NOT_PENDING`, `OFFICE_STEP`, `NOT_OFFICE_STEP`,
 * `NOT_APPROVER`, `SELF_APPROVAL_FORBIDDEN`, `SAME_APPROVER_FORBIDDEN`. Parola, içerik hash'i,
 * bayat ekran ve gerekçe karar anında denetlenir; satırın görünmesini etkilemez.
 */
export function decisionBlocker(input: MayDecideInput): string | null {
  const { state } = input;
  if (state.status !== 'pending') return 'NOT_PENDING';
  const side = sideBlocker(input.rule, state.step, input.at);
  if (side) return side.code;
  const role = approverRole(state, input.rule, input.approver, input.recordParties);
  return 'code' in role ? role.code : null;
}

/**
 * Kişi bu adıma şimdi karar verebilir mi (ADR 0156): kayıt ekranındaki karar düğmesi ve panodaki
 * "bana düşen iş" satırı aynı işlevi kullanır; yazma yolu (`decideApproval`) aynı kuralları uygular.
 */
export function mayDecide(input: MayDecideInput): boolean {
  return decisionBlocker(input) === null;
}

/** Onay ya da iade kararı; yeni değiştirilemez onay kaydını döner (mimari belge §7.2, §7.3). */
export function decideApproval(input: DecideInput): Approval {
  const { state, rule, approver } = input;
  if (state.status !== 'pending') throw new DomainError('NOT_PENDING', state.status);
  if (
    input.expected &&
    (input.expected.submissionId !== state.submission.id || input.expected.step !== state.step)
  ) {
    throw new DomainError('STALE_DECISION', `adım ${state.step}`);
  }
  const side = sideBlocker(rule, state.step, input.at);
  if (side) throw new DomainError(side.code, side.message);
  // Parola kayıt türüne göre (ADR 0056 §1, §3)
  assertReauthenticated(rule.recordType, input.reauthenticated, input.reauthPolicy);
  if (input.contentHash !== state.submission.contentHash) {
    throw new DomainError('CONTENT_CHANGED');
  }
  // Rol, giren-onaylayan ve dört göz (ADR 0026 §3, ADR 0071 §4): `mayDecide` ile aynı kural
  const role = approverRole(state, rule, approver, input.recordParties);
  if ('code' in role) throw new DomainError(role.code, role.message);
  const { roleId } = role;
  const reason = input.reason?.trim() ?? '';
  // İadede gerekçe zorunlu (mimari belge §7.2). Doğrulanmama nedeni iade gerekçesidir
  // [MEPC.312 4.3.10; ADR 0042 §16]
  if (input.decision === 'returned' && reason === '')
    throw new DomainError('RETURN_REASON_REQUIRED');
  // Süresi geçmiş adımda açıklama (ADR 0042 §16; ajan kararı) [sorular #241]
  if (input.lateReasonRequired && reason === '' && isApprovalOverdue(state, rule, input.now)) {
    throw new DomainError('LATE_REASON_REQUIRED', `adım ${state.step}`);
  }
  return {
    id: input.id,
    submissionId: state.submission.id,
    step: state.step,
    decision: input.decision,
    reason: reason === '' ? null : reason,
    approverId: approver.userId,
    approverRoleId: roleId,
    matrixVersion: input.matrixVersion,
    contentHash: input.contentHash,
    at: input.now,
  };
}

export interface ReopenInput {
  state: ApprovalState;
  rule: ApprovalRule;
  id: string;
  reason: string;
  /** Açan kişi ve bu yetkiyi veren rolü. */
  approver: { userId: string; roleId: string };
  contentHash: string;
  reauthenticated: boolean;
  /** Şirketin onay ayarı; verilmezse her tür zorunlu (ADR 0056 §4). */
  reauthPolicy?: ApprovalReauthPolicy;
  matrixVersion: number;
  now: string;
}

/**
 * Kesinleşmiş kaydı gerekçeyle yeniden açar (ADR 0022 §3): son gönderime değiştirilemez bir iade
 * kaydı yazılır, kayıt "iade edildi" durumuna düşer, düzeltilip yeniden gönderilir; geçmiş korunur.
 * Gerekçe zorunludur (mimari belge §7.2); parola kayıt türüne göredir (ADR 0056).
 */
export function reopenApproved(input: ReopenInput): Approval {
  const { state } = input;
  if (state.status !== 'approved') throw new DomainError('NOT_FINAL', state.status);
  assertReauthenticated(input.rule.recordType, input.reauthenticated, input.reauthPolicy);
  if (input.contentHash !== state.submission.contentHash) throw new DomainError('CONTENT_CHANGED');
  const reason = input.reason.trim();
  if (reason === '') throw new DomainError('REOPEN_REASON_REQUIRED');
  return {
    id: input.id,
    submissionId: state.submission.id,
    step: input.rule.steps.length + 1,
    decision: 'returned',
    reason,
    approverId: input.approver.userId,
    approverRoleId: input.approver.roleId,
    matrixVersion: input.matrixVersion,
    contentHash: input.contentHash,
    at: input.now,
  };
}

/**
 * Bekleyen onay, matristeki süreyi aştı mı (MEPC.312 4.3.10). Bekleme önceki adımın onayından, ilk
 * adımda gönderimden başlar (`since`; mimari belge §7.3).
 */
export function isApprovalOverdue(state: ApprovalState, rule: ApprovalRule, now: string): boolean {
  if (state.status !== 'pending' || rule.deadlineHours === undefined) return false;
  return Date.parse(now) - Date.parse(state.since) > rule.deadlineHours * 3_600_000;
}
